import React from 'react';
import {
  getPaginationConfig,
  calculatePagination,
  getVisiblePageNumbers,
  getPaginationInfo,
  getPaginationActions,
  PaginationConfig,
  PaginationState
} from '../../utils/reportPortalToolkit';
import { Button } from '@mui/material';

// Import navigation icons
import { 
  KeyboardDoubleArrowLeft as FirstIcon,
  KeyboardArrowLeft as PreviousIcon,
  KeyboardArrowRight as NextIcon,
  KeyboardDoubleArrowRight as LastIcon
} from '@mui/icons-material';

interface ToolkitPaginationProps {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  paginationType?: string;
  theme?: 'light' | 'dark';
  className?: string;
  showPageInfo?: boolean;
}

const ToolkitPagination: React.FC<ToolkitPaginationProps> = ({
  currentPage,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  paginationType = 'default',
  theme = 'light',
  className = '',
  showPageInfo = true
}) => {
  const config = getPaginationConfig(paginationType);
  const pagination = calculatePagination(currentPage, pageSize, totalItems);
  const visiblePages = getVisiblePageNumbers(currentPage, pagination.totalPages, config.maxVisiblePages);
  const pageInfo = getPaginationInfo(currentPage, pageSize, totalItems);
  const actions = getPaginationActions(currentPage, pagination.totalPages);

  if (totalItems === 0) {
    return null;
  }

  return (
    <div className={`toolkit-pagination ${className}`} style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
      {/* Page Size Selector */}
      {config.showPageSizeSelector && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '0.875rem', color: theme === 'light' ? '#666' : '#ccc' }}>
            Show:
          </span>
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            style={{
              padding: '4px 8px',
              border: `1px solid ${theme === 'light' ? '#ddd' : '#555'}`,
              borderRadius: '4px',
              backgroundColor: theme === 'light' ? '#fff' : '#333',
              color: theme === 'light' ? '#333' : '#fff'
            }}
          >
            {config.pageSizeOptions.map(size => (
              <option key={size} value={size}>{size}</option>
            ))}
          </select>
        </div>
      )}

      {/* Page Info */}
      {showPageInfo && (
        <span style={{ fontSize: '0.875rem', color: theme === 'light' ? '#666' : '#ccc' }}>
          {pageInfo.pageInfo}
        </span>
      )}

      {/* Navigation Buttons */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        {/* First Button */}
        {config.showFirstButton && (
          <Button
            variant="outlined"
            size="small"
            disabled={!actions.canGoFirst}
            onClick={() => actions.canGoFirst && onPageChange(1)}
            startIcon={<FirstIcon />}
          >
            First
          </Button>
        )}

        {/* Previous Button */}
        <Button
          variant="outlined"
          size="small"
          disabled={!actions.canGoPrevious}
          onClick={() => actions.canGoPrevious && onPageChange(currentPage - 1)}
          startIcon={<PreviousIcon />}
        >
          Previous
        </Button>

        {/* Page Numbers */}
        {visiblePages.map(page => (
          <Button
            key={page}
            variant={page === currentPage ? 'contained' : 'outlined'}
            size="small"
            onClick={() => onPageChange(page)}
          >
            {page}
          </Button>
        ))}

        {/* Next Button */}
        <Button
          variant="outlined"
          size="small"
          disabled={!actions.canGoNext}
          onClick={() => actions.canGoNext && onPageChange(currentPage + 1)}
          endIcon={<NextIcon />}
        >
          Next
        </Button>
      </div>

      {/* Last Button */}
      {config.showLastButton && (
        <Button
          variant="outlined"
          size="small"
          disabled={!actions.canGoLast}
          onClick={() => actions.canGoLast && onPageChange(pagination.totalPages)}
          endIcon={<LastIcon />}
        >
          Last
        </Button>
      )}
    </div>
  );
};

export default ToolkitPagination;
